import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/ui/Card'
import { Pill } from '../components/ui/Pill'
import { Avatar } from '../components/ui/Avatar'
import { GatedButton } from '../components/ui/GatedButton'
import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/EmptyState'
import { useToast } from '../contexts/ToastContext'
import { usePerson } from '../contexts/PersonContext'
import { useDirectory, useCreateFriendRequest } from '../hooks/useFriends'
import type { DirectoryEntryDto } from '../types/community'

const FILTERS = [
  { label: 'Everyone', value: '' },
  { label: 'Minecraft', value: 'minecraft' },
  { label: 'Wobbly Life', value: 'wobbly life' },
  { label: 'Mecha Chameleon', value: 'mecha chameleon' },
  { label: 'Co-op · calm', value: 'co-op / calm' },
]

/** Find friends (COTC_FriendsApprovals R5 / DESIGN §3.10), kid role. */
export function FindFriendsPage() {
  const { activePerson, guardianFirst, flags } = usePerson()
  const { flash } = useToast()
  const qc = useQueryClient()
  const [filter, setFilter] = useState('')
  const actingId = activePerson?.id
  const directory = useDirectory(actingId, filter)
  const createReq = useCreateFriendRequest()
  const entries = directory.data ?? []
  const [pending, setPending] = useState<Record<string, boolean>>({})
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const inFlight = useRef(new Set<string>())
  const generation = useRef(0)
  useEffect(() => { const current = ++generation.current; inFlight.current.clear(); setPending({}); setRowErrors({}); return () => { generation.current = current + 1 } }, [actingId])

  const kidFirst = flags.first || 'they'

  function ctaLabel(state: DirectoryEntryDto['state']): string {
    if (state === 'friends') return 'Friends ✓'
    if (state === 'pendingOwn') return `Waiting on ${guardianFirst || 'a grown-up'}`
    if (state === 'pendingTheirs') return 'Request sent'
    if (flags.viewOnly) return `Ask ${guardianFirst || 'a grown-up'} to send this`
    if (flags.supervised) return 'Ask to be friends'
    return 'Send a friend request'
  }

  function patchState(personId: string, state: DirectoryEntryDto['state']) {
    qc.setQueryData<DirectoryEntryDto[]>(['community', 'directory', actingId ?? '', filter], (old) =>
      (old ?? []).map((e) => (e.personId === personId ? { ...e, state } : e)),
    )
  }

  async function onCta(entry: DirectoryEntryDto) {
    if (entry.state === 'friends') { flash('Already friends — find them under My friends.'); return }
    if (entry.state === 'pendingOwn' || entry.state === 'pendingTheirs') {
      flash(`Already sent — ${guardianFirst || 'a grown-up'} will let you know.`); return
    }
    if (flags.viewOnly) {
      // View-only never sends; no fictional guardian nudge.
      flash(`${guardianFirst || 'A grown-up'} sends requests for ${kidFirst}. Ask them to send this request.`)
      return
    }
    if (inFlight.current.has(entry.personId)) return
    inFlight.current.add(entry.personId)
    const current = generation.current
    setPending(old => ({ ...old, [entry.personId]: true })); setRowErrors(old => ({ ...old, [entry.personId]: '' }))
    try {
      const res = await createReq.mutateAsync({ targetPersonId: entry.personId })
      if (current !== generation.current) return
      if (res.state === 'AwaitingOwnGuardian') {
        patchState(entry.personId, 'pendingOwn')
        flash(`Sent to ${guardianFirst || 'a grown-up'} to approve.`)
      } else {
        patchState(entry.personId, 'pendingTheirs')
        flash('Request sent to their parent.')
      }
    } catch {
      if (current === generation.current) setRowErrors(old => ({ ...old, [entry.personId]: 'That request did not go through. Please try again.' }))
    } finally {
      if (current === generation.current) { inFlight.current.delete(entry.personId); setPending(old => ({ ...old, [entry.personId]: false })) }
    }
  }

  return (
    <div className="animate-rise px-4 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-fainter">Find friends</div>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">Find someone to play with</h1>
      <p className="mt-3 max-w-[60ch] text-[15px] leading-relaxed text-ink-muted">
        Find people in circles your family shares. First names and ages are shown here.{' '}
        {flags.viewOnly
          ? `${guardianFirst || 'A grown-up'} sends requests for you, and the other family decides.`
          : flags.supervised
            ? `${guardianFirst || 'A grown-up'} reviews your requests before the other family's approval.`
            : "Send a friend request for the other family's approval."}
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            aria-pressed={filter === f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-pill px-3 py-1.5 font-mono text-[11px] uppercase tracking-label transition-colors ${filter === f.value ? 'bg-ink text-paper-panel' : 'bg-chip text-chip-fg hover:text-ink'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {directory.isLoading ? (
        <p className="mt-8 font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
      ) : directory.isError ? (<div role="alert" className="mt-6 text-[13px] text-honey-fg">Could not load the directory. <Button variant="outline" size="sm" onClick={() => void directory.refetch()}>Retry directory</Button></div>) : entries.length === 0 ? (
        <EmptyState
          className="mt-8"
          dashed
          description="Nobody to show here yet. Join a circle or try a different game — new families turn up as your circles grow."
        />
      ) : (
        <div className="mt-6 grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(100%,300px),1fr))]">
          {entries.map((e) => (
            <Card key={e.personId} className="flex min-w-0 flex-col gap-3 break-words">
              <div className="flex items-start gap-3">
                <Avatar initials={(e.firstName || '?')[0].toUpperCase()} tintIndex={e.tintIndex} size={44} ringClass="border-paper-surface" />
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-ink">{e.firstName}</div>
                  <div className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
                    {[e.ageYears != null ? `${e.ageYears}` : null, e.vouchLine ? 'vouched' : null].filter(Boolean).join(' · ') || 'new here'}
                  </div>
                </div>
              </div>
              {e.blurb && <p className="text-[13px] leading-relaxed text-ink-muted">{e.blurb}</p>}
              {e.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">{e.tags.map((t) => <Pill key={t}>{t}</Pill>)}</div>
              )}
              {e.vouchLine && <p className="text-[12px] text-moss">{e.vouchLine}</p>}
              {rowErrors[e.personId] && <p role="alert" className="text-[13px] text-honey-fg">{rowErrors[e.personId]}</p>}
              {e.state === 'friends' ? (
                <Button variant="approve" size="sm" onClick={() => onCta(e)}>{ctaLabel(e.state)}</Button>
              ) : (
                <GatedButton
                  disabled={pending[e.personId] === true}
                  gated={e.state === 'pendingOwn' || e.state === 'pendingTheirs'}
                  hint={`Already sent — ${guardianFirst || 'a grown-up'} will let you know.`}
                  variant="accent"
                  onClick={() => onCta(e)}
                >
                  {pending[e.personId] ? 'Sending…' : ctaLabel(e.state)}
                </GatedButton>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
